<!-- This is the partial which contains the fields for the Proposed Insured and Policy Owner. -->
<template>
	<FormKit
		v-model="identityForm"
		:actions="false"
		ref="form"
		type="form"
		class="w-full"
	>
		<div class="grid grid-cols-3 gap-4 *:w-full *:max-w-none">
			<FormKit
				type="text"
				name="firstName"
				:label="t('document.firstName')"
				label-class="required"
				validation="required"
			/>
			<FormKit
				type="text"
				name="middleName"
				:label="t('document.middleName')"
			/>
			<FormKit
				type="text"
				name="lastName"
				:label="t('document.lastName')"
				label-class="required"
				validation="required"
			/>
		</div>
		<div v-if="!truncate">
			<div class="grid grid-cols-2 gap-4 *:w-full *:max-w-none">
				<FormKit
					type="date"
					name="birthDate"
					:label="t('document.dateOfBirth')"
					label-class="required"
					format="MM / DD / YYYY"
					placeholder="MM / DD / YYYY"
					value-format="YYYY-MM-DD"
					value-locale="en-US"
					validation="required"
				/>
				<FormKit
					overlay
					type="mask"
					name="socialSecurityNumber"
					:label="t('plan.ssn')"
					mask="###-##-####"
					overlay-placeholder-class="text-secondary-400"
					overlay-literal-class="text-secondary-400"
				/>
			</div>
			<div class="grid grid-cols-4 gap-4 *:w-full *:max-w-none">
				<FormKit
					type="text"
					name="address"
					:label="t('document.address')"
					label-class="required"
					validation="required"
				/>
				<FormKit
					type="text"
					name="city"
					:label="t('person.city')"
					label-class="required"
					validation="required"
				/>
				<FormKit
					type="dropdown"
					name="state"
					:label="t('person.state')"
					label-class="required"
					:options="stateOptions"
					validation="required"
				/>
				<FormKit
					overlay
					type="mask"
					name="zip"
					:label="t('person.zip')"
					label-class="required"
					mask="#####"
					overlay-placeholder-class="text-white"
					validation="required|matches:/^\d{5}(-\d{4})?$/"
				/>
			</div>
			<div class="grid grid-cols-3 gap-4 *:w-full *:max-w-none">
				<FormKit
					type="email"
					name="email"
					:label="t('person.email')"
					validation="email"
				/>
				<FormKit
					overlay
					type="mask"
					name="phone"
					:label="t('document.primaryPhone')"
					label-class="required"
					mask="###-###-####"
					overlay-placeholder-class="text-transparent"
					overlay-literal-class="text-secondary-400"
					validation="required|matches:/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/"
					:validation-messages="{
						matches: 'Phone number must be in the format xxx-xxx-xxxx',
					}"
				/>
				<FormKit
					overlay
					type="mask"
					name="secondaryPhone"
					:label="t('document.secondaryPhone')"
					mask="###-###-####"
					overlay-placeholder-class="text-transparent"
					overlay-literal-class="text-secondary-400"
					validation="matches:/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/"
					:validation-messages="{
						matches: 'Phone number must be in the format xxx-xxx-xxxx',
					}"
				/>
			</div>
		</div>
	</FormKit>
</template>

<script setup lang="ts">
import { stateOptions } from '@/data/stateOptions'
import { usePresentationLocale } from '@/i18n'
import { computed, ref, watch } from 'vue'

const { t } = usePresentationLocale()

interface Props {
	modelValue: Record<string, any>
	truncate?: boolean
}

const props = defineProps<Props>()

// On edit, these events will be emitted to ConfirmationStep.
const emit = defineEmits<{
	'update:modelValue': [value: Record<string, any>]
	validation: [value: boolean]
}>()
const identityForm = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})

const form = ref()
const validState = computed(() => form.value?.node?.context?.state?.valid)

watch(
	validState,
	() => {
		emit('validation', validState.value)
	},
	{ deep: true, immediate: true }
)
</script>

<style scoped>
@reference "tailwindcss";
:deep(.formkit-outer:not([data-type='checkbox']) .formkit-label) {
	@apply pb-2;
}

:deep(.formkit-label.required::after) {
	content: '*';
	@apply text-red-400;
	margin-left: 6px;
}
</style>
